import { getSalonAdminApiBaseUrl } from '../../salon-admin/utils/apiConfig';
import { fetchWithTimeout } from '../../salon/utils/fetchWithTimeout';
import { franchiseAuthHeaders } from '../../shared/franchiseFlowAuth';
import { portalFetchInit } from '../../admin-shared/portalApiAuth';

function adminBase() {
  return (getSalonAdminApiBaseUrl() || '').replace(/\/$/, '');
}

async function parseJson(res) {
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  }
  return data;
}

/** Kiosk device session (franchise-flow cookie). */
export async function getPassportKioskScanState() {
  const base = adminBase();
  if (!base) throw new Error('Salon admin API is not configured');
  const res = await fetchWithTimeout(`${base}/franchise-flow/passport-kiosk/scan`, {
    method: 'GET',
    credentials: 'include',
    headers: franchiseAuthHeaders(),
    timeoutMs: 12000,
  });
  return parseJson(res);
}

export async function disablePassportKioskScanAfterCapture() {
  const base = adminBase();
  if (!base) return;
  try {
    const res = await fetchWithTimeout(`${base}/franchise-flow/passport-kiosk/scan/disable`, {
      method: 'POST',
      credentials: 'include',
      headers: franchiseAuthHeaders({ 'Content-Type': 'application/json' }),
      body: '{}',
      timeoutMs: 8000,
    });
    await parseJson(res);
  } catch {
    /* non-blocking */
  }
}

/** Partner front desk (portal session). */
export async function setPassportKioskScanState(scanEnabled, portalId) {
  const base = adminBase();
  if (!base) throw new Error('Salon admin API is not configured');
  const res = await fetchWithTimeout(
    `${base}/franchise-flow/passport-kiosk/scan`,
    portalFetchInit(portalId, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ scan_enabled: Boolean(scanEnabled) }),
      timeoutMs: 12000,
    }),
  );
  return parseJson(res);
}

export async function getPassportKioskScanStateForPartner(portalId) {
  const base = adminBase();
  if (!base) throw new Error('Salon admin API is not configured');
  const res = await fetchWithTimeout(
    `${base}/franchise-flow/passport-kiosk/scan`,
    portalFetchInit(portalId, { method: 'GET', timeoutMs: 12000 }),
  );
  return parseJson(res);
}

export async function uploadPassportIdleVideo(file, portalId) {
  const base = adminBase();
  if (!base) throw new Error('Salon admin API is not configured');
  if (!file) throw new Error('Video file is required');
  const body = new FormData();
  body.append('video', file);
  const res = await fetchWithTimeout(
    `${base}/franchise-flow/passport-kiosk/idle-video`,
    portalFetchInit(portalId, {
      method: 'POST',
      body,
      timeoutMs: 120000,
    }),
  );
  return parseJson(res);
}

export async function deletePassportIdleVideo(portalId) {
  const base = adminBase();
  if (!base) throw new Error('Salon admin API is not configured');
  const res = await fetchWithTimeout(
    `${base}/franchise-flow/passport-kiosk/idle-video`,
    portalFetchInit(portalId, { method: 'DELETE', timeoutMs: 20000 }),
  );
  return parseJson(res);
}

export const PASSPORT_IDLE_VIDEO_DELAY_KEY = 'passport_idle_video_delay_until';

export function schedulePassportIdleVideoDelay(ms = 10000) {
  try {
    sessionStorage.setItem(PASSPORT_IDLE_VIDEO_DELAY_KEY, String(Date.now() + Math.max(0, ms)));
  } catch {
    /* ignore */
  }
}

export function consumePassportIdleVideoDelayMs() {
  try {
    const until = Number(sessionStorage.getItem(PASSPORT_IDLE_VIDEO_DELAY_KEY) || 0);
    if (!until) return 0;
    const remaining = until - Date.now();
    if (remaining <= 0) {
      sessionStorage.removeItem(PASSPORT_IDLE_VIDEO_DELAY_KEY);
      return 0;
    }
    return remaining;
  } catch {
    return 0;
  }
}

export function clearPassportIdleVideoDelay() {
  try {
    sessionStorage.removeItem(PASSPORT_IDLE_VIDEO_DELAY_KEY);
  } catch {
    /* ignore */
  }
}
